<template>
  <div class="modal" @click.self.stop="closeModal">
    <div class="modal-content" @click.stop>
      <h2>Remplacer {{ item.dish.name }} par un autre plat de même profil nutritionnel ...</h2>

      <div style="display: flex; gap: 10px; margin-bottom: 20px;">
        <button class="button" @click="gotoRandomMode">
          ... parmi ces suggestions
        </button>

        <button class="button" @click="gotoLikedMode">
          ... parmi mes préférés
        </button>

        <button class="button" @click="gotoAlimentMode">
          ... contenant un ingrédient
        </button>
      </div>

      <div v-if="mode == 'aliment'" class="content">
        <label class="form-label">Rechercher des recettes contenant un ingrédient en particulier</label>

        <input
          class="form-input"
          ref="searchInput"
          style="margin-bottom: 20px;"
          placeholder="Rechercher des recettes contenant ingrédient en particulier..."
          @input="searchForRecipesWithQuery($event.target.value)"
        />

        <LoaderView v-if="searchingForRecipesWithAliment" />

        <div v-else-if="recipesWithAliment === null"></div>

        <div v-else-if="recipesWithAliment.length === 0">
          <p>Rupture de recette avec cet ingrédient : j’en choisis un autre</p>
        </div>

        <div v-else class="dish-container">
          <div
            v-for="recipe in recipesWithAliment"
            :key="recipe.id"
            class="card liked-dish"
            @click="selectedRecipe = recipe"
            :style="{ backgroundImage: 'url(' + mediaSrc(recipe.photo) + ')' }"
          >
            <div class="overlay">
              <div class="liked-dish-name">{{ recipe.name }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="mode == 'random'" class="content">
        <LoaderView v-if="searchingForRandomRecipes" />

        <div v-if="randomRecipes === null"></div>

        <div v-else-if="randomRecipes.length === 0">
          <p>Oups, cette semaine rupture de plat alternatif à proposer ! Puis-je simplifier mes préférences ?</p>
        </div>

        <div v-else class="dish-container">
          <div
            v-for="recipe in randomRecipes"
            :key="recipe.id"
            class="card liked-dish"
            @click="selectedRecipe = recipe"
            :style="{ backgroundImage: 'url(' + mediaSrc(recipe.photo) + ')' }"
          >
            <div class="overlay">
              <div class="liked-dish-name">{{ recipe.name }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="mode == 'liked'" class="content">
        <LoaderView v-if="searchingForLikedRecipes" />

        <div v-if="likedRecipes === null"></div>

        <div v-else-if="likedRecipes.length === 0">
          <p>J’ajoute des plats favoris pour que Mes Meilleurs Menus puisse me les reproposer</p>
        </div>

        <div v-else class="dish-container">
          <div
            v-for="recipe in likedRecipes"
            :key="recipe.id"
            class="card liked-dish"
            @click="selectedRecipe = recipe"
            :style="{ backgroundImage: 'url(' + mediaSrc(recipe.photo) + ')' }"
          >
            <div class="overlay">
              <div class="liked-dish-name">{{ recipe.name }}</div>
            </div>
          </div>
        </div>
      </div>

      <div style="display: flex; justify-content: flex-end; margin-top: 1rem;">
        <button class="button gray" @click="closeModal">Annuler</button>
      </div>
    </div>

    <div v-if="selectedRecipe" class="modal">
      <div class="modal-content">
        <h2>
          Etes-vous sûr de vouloir remplacer {{ item.dish.name }} par {{ selectedRecipe.name }} ?
        </h2>

        <div style="display: flex; justify-content: flex-end; margin-top: 1rem; gap: 10px;">
          <button class="button gray" @click="selectedRecipe = null">Annuler</button>
          <button class="button" @click="replace">Oui</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoaderView from "@/components/LoaderView.vue";
import { api } from "@/api";

export default {
  name: "ReplaceDishModal",
  components: { LoaderView },

  props: {
    item: {
      type: Object,
      required: true,
    },
    date: String,
  },

  data() {
    return {
      mode: null,
      searchingForRecipesWithAliment: false,
      searchingForRandomRecipes: false,
      searchingForLikedRecipes: false,
      recipesWithAliment: null,
      randomRecipes: null,
      likedRecipes: null,
      query: null,
      selectedRecipe: null,
      searchTimeout: null,
    };
  },

  mounted() {
    this.gotoRandomMode();
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  methods: {
    closeModal() {
      this.$emit("close");
    },

    gotoRandomMode() {
      this.mode = "random";
      this.searchForRandomRecipes();
    },

    gotoLikedMode() {
      this.mode = "liked";
      if (this.likedRecipes === null) this.searchForLikedRecipes();
    },

    gotoAlimentMode() {
      this.mode = "aliment";
      setTimeout(() => this.$refs.searchInput?.focus(), 400);
    },

    searchForRandomRecipes() {
      this.searchingForRandomRecipes = true;

      api
        .get(`replacement-suggestions/`, {
          params: {
            dish_id: this.item.dish.id,
            meal: this.item.meal,
            date: this.date,
            type: this.item.dish.dish_type,
          },
        })
        .then((res) => {
          this.randomRecipes = res.data.slice(0, 3);
        })
        .finally(() => {
          this.searchingForRandomRecipes = false;
        });
    },

    searchForLikedRecipes() {
      this.searchingForLikedRecipes = true;

      api
        .get(`replacement-suggestions/`, {
          params: {
            dish_id: this.item.dish.id,
            meal: this.item.meal,
            date: this.date,
            type: this.item.dish.dish_type,
            only_favorites: true,
          },
        })
        .then((res) => {
          this.likedRecipes = res.data.slice(0, 3);
        })
        .finally(() => {
          this.searchingForLikedRecipes = false;
        });
    },

    searchForRecipesWithQuery(query) {
      this.query = query;

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      const trimmedQuery = query.trim();

      if (!trimmedQuery) {
        this.searchingForRecipesWithAliment = false;
        this.recipesWithAliment = null;
        return;
      }

      this.searchTimeout = setTimeout(() => {
        this.fetchRecipesWithQuery(trimmedQuery);
      }, 250);
    },

    fetchRecipesWithQuery(query) {
      this.searchingForRecipesWithAliment = true;

      api
        .get(`replacement-suggestions/`, {
          params: {
            dish_id: this.item.dish.id,
            meal: this.item.meal,
            date: this.date,
            type: this.item.dish.dish_type,
            q: query,
          },
        })
        .then((res) => {
          if (query === this.query.trim()) {
            this.recipesWithAliment = res.data.slice(0, 3);
          }
        })
        .finally(() => {
          this.searchingForRecipesWithAliment = false;
        });
    },

    mediaSrc(uri) {
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },

    replace() {
      this.replacing = true;

      api
        .post(`/replace-my-item/${this.item.id}/`, {
          recipe_id: this.selectedRecipe.id,
        })
        .then((res) => {
          this.$emit("update", res.data);
          this.closeModal();
        })
        .finally(() => {
          this.replacing = false;
        });
    },
  },
};
</script>

<style scoped>
h2 {
  font-size: 20px;
}

.liked-dish {
  position: relative;
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  flex: 1;
}

.liked-dish:hover {
  transform: scale(1.01);
}

.overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease;
}

.liked-dish:hover .overlay {
  background-color: rgba(255, 255, 255, 0.7);
}

.liked-dish-name {
  font-size: 20px;
  font-weight: bold;
  color: #000;
  text-align: center;
  padding: 10px;
}

.content {
  height: 50vh;
  display: flex;
  flex-direction: column;
}

.dish-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}
</style>
